import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Row,
} from "reactstrap";
import { getOrderByIdAPI, updateOrderStatusAPI } from "../../../../../api/adminOrderAPIs.ts";
import { Icon } from "../../../../../components/Component";
import Content from "../../../../../layout/content/Content";
import Head from "../../../../../layout/head/Head";

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        const response = await getOrderByIdAPI({ id });
        setOrder(response?.data?.data);
        setStatus(response?.data?.data?.orderStatus || "");
      } catch (error) {
        console.error("Failed to fetch order details:", error);
        toast.error("Failed to load order details");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [id]);

  const handleStatusUpdate = async () => {
    try {
      await updateOrderStatusAPI({
        id,
        body: { orderStatus: status },
      });
      setOrder((prev) => ({ ...prev, orderStatus: status }));
      toast.success(`Order status updated to ${status}`);
    } catch (error) {
      console.error("Failed to update order status:", error);
      toast.error("Failed to update order status");
    }
  };

  const statusOptions = [
    "Pending",
    "Confirmed",
    "Shipped",
    "Delivered",
    "Cancelled",
    "Return Requested",
  ];

  if (loading) {
    return (
      <React.Fragment>
        <Head title="Order Details" />
        <Content>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Content>
      </React.Fragment>
    );
  }

  if (!order) {
    return (
      <React.Fragment>
        <Head title="Order Details" />
        <Content>
          <div className="text-center py-4">
            <p className="text-danger mb-3">Order not found</p>
            <Button color="primary" onClick={() => navigate("/orders")}>
              Back to Order List
            </Button>
          </div>
        </Content>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Head title={`Order Details - ${order?._id}`} />
      <Content>
        <div className="container-fluid py-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="text-primary">Order #{order?._id}</h3>
            <Button
              color="secondary"
              onClick={() => navigate("/orders")}
              className="btn-icon"
            >
              <Icon name="arrow-left" /> Back to Orders
            </Button>
          </div>

          <Row>
            {/* Order Info */}
            <Col lg={8}>
              <Card className="shadow-sm border-0 mb-4">
                <CardBody>
                  <CardTitle tag="h5" className="text-dark fw-bold mb-4">
                    Order Information
                  </CardTitle>
                  <Row>
                    <Col md={6}>
                      <div className="mb-4">
                        <strong className="text-muted d-block mb-1">Customer:</strong>
                        <p className="mb-1">{order?.userDetails?.name}</p>
                        <p className="text-muted">{order?.userDetails?.email}</p>
                        <p className="text-muted">{order?.userDetails?.phone}</p>
                      </div>
                      <div className="mb-4">
                        <strong className="text-muted d-block mb-1">Order Date:</strong>
                        <p>{new Date(order?.orderDate).toLocaleString()}</p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-4">
                        <strong className="text-muted d-block mb-1">Total Amount:</strong>
                        <p className="text-dark fw-bold">₹{order?.totalAmount?.toFixed(2)}</p>
                      </div>
                      <div className="mb-4">
                        <strong className="text-muted d-block mb-1">Payment:</strong>
                        <p>
                          {order?.payment_id?.paymentMethod} ({order?.payment_id?.status})
                        </p>
                        <p className="text-muted">Transaction: {order?.payment_id?.transactionId}</p>
                      </div>
                      <div className="mb-4">
                        <strong className="text-muted d-block mb-1">Shipping:</strong>
                        <p>{order?.courierService}</p>
                        <p className="text-muted">
                          Est. Delivery: {order?.estimatedDeliveryDays} days
                        </p>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* Product List */}
              <Card className="shadow-sm border-0 mb-4">
                <CardBody>
                  <CardTitle tag="h5" className="text-dark fw-bold mb-4">
                    Products
                  </CardTitle>
                  <ListGroup flush>
                    {order?.products?.map((product) => (
                      <ListGroupItem
                        key={product?._id}
                        className="d-flex align-items-center py-3"
                      >
                        <img
                          src={product?.productId?.images?.[0]?.url}
                          alt={product?.name}
                          className="me-3"
                          style={{
                            width: "60px",
                            height: "60px",
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                        />
                        <div className="flex-grow-1">
                          <p className="fw-bold mb-1">{product?.name}</p>
                          <small className="text-muted">
                            Weight: {product?.skuParameters?.weight}kg
                          </small>
                        </div>
                        <div className="text-end">
                          <p className="mb-1">Qty: {product?.quantity}</p>
                          <p className="text-dark fw-bold">
                            ₹{(product?.price * product?.quantity).toFixed(2)}
                          </p>
                        </div>
                      </ListGroupItem>
                    ))}
                  </ListGroup>
                </CardBody>
              </Card>
            </Col>

            {/* Status and Actions */}
            <Col lg={4}>
              <Card className="shadow-sm border-0">
                <CardBody>
                  <CardTitle tag="h5" className="text-dark fw-bold mb-4">
                    Order Management
                  </CardTitle>
                  <FormGroup>
                    <Label for="orderStatus" className="text-muted fw-medium">
                      Update Status
                    </Label>
                    <Input
                      type="select"
                      name="orderStatus"
                      id="orderStatus"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className="mb-3"
                    >
                      {statusOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Input>
                    <Button
                      color="primary"
                      onClick={handleStatusUpdate}
                      disabled={status === order?.orderStatus}
                      className="w-100"
                    >
                      Update Status
                    </Button>
                  </FormGroup>
                  <div className="mt-4">
                    <h6 className="text-muted fw-medium">Order Status:</h6>
                    <Badge
                      color={
                        order?.orderStatus === "Delivered"
                          ? "success"
                          : order?.orderStatus === "Cancelled"
                          ? "danger"
                          : "warning"
                      }
                      className="px-3 py-2"
                    >
                      {order?.orderStatus}
                    </Badge>
                  </div>
                  <div className="mt-3">
                    <h6 className="text-muted fw-medium">Shipping Status:</h6>
                    <Badge
                      color={
                        order?.shippingStatus === "Delivered"
                          ? "success"
                          : order?.shippingStatus === "Cancelled"
                          ? "danger"
                          : "warning"
                      }
                      className="px-3 py-2"
                    >
                      {order?.shippingStatus}
                    </Badge>
                  </div>
                  <div className="mt-3">
                    <h6 className="text-muted fw-medium">ShipRocket Order ID:</h6>
                    <p className="mb-0">{order?.shipRocketOrderId}</p>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </Content>
    </React.Fragment>
  );
};
export default OrderDetails;